// ARRANGEMENT PREVIEW — a plan rendered WITHOUT applying it (CR-20260827-001, reworked by
// CR-20260830-002 when the decomposition moved to the backend).
//
// The Order Verification Dialog shows the backend's answer read-only before any Re-group dialog
// exists. AVAILABILITY IS NO LONGER JUDGED HERE: the backend's rule-A verdict decides it —
// `ArrangementPlanPages.status === 'ok'` means the best cover differs from the screen and there
// is something to show; 'unchanged' means there is not. This file only turns ONE plan page into
// displayable preview orders.
//
// ⚠⚠ THIS FILE MUST NOT SETTLE A SOURCE ROW. Applying a plan means constructing `DropZoneOrder`s,
// whose constructor calls `adjustSettledQty` on the source wrapper. This file only READS the
// plan's legs. Build preview rows from wrapper fields; never construct a `DropZoneOrder` here.
//
// ORDERING MIRRORS THE RE-GROUP DIALOG EXACTLY, because the verification preview promises to
// show what applying that plan will later produce: legs sorted by `sortLegs`, orders ranked by
// `sortBoxes`, names dealt by position via `zoneTitleForIndex`. One divergence and the preview
// is a forgery.

import {ArrangementPlan} from './arrangement-planner';
import {sortBoxes, sortLegs, zoneTitleForIndex} from './leg-order';
import {findHCF} from '../utils';

/** One leg of a previewed order. `qty` is SIGNED - buy positive, sell negative. */
export interface ArrangementPreviewLeg {
    ticker: string;
    strike: string;
    expiration: string;
    type: 'Call' | 'Put';
    qty: number;
}

/** One previewed order - what a drop zone would hold after the plan is applied. */
export interface ArrangementPreviewOrder {
    /** Position-pinned name: 'Main', 'Linked #1', … - same rule as the Re-group dialog. */
    title: string;
    legs: ArrangementPreviewLeg[];
    hcf: number;
    /**
     * True when this order IS the pile's naked-leg order (planner shape 'naked'): its legs close
     * into no shape. The screen flags it and tells the user to execute it before the others.
     */
    naked: boolean;
}

/**
 * Render one plan page as preview orders, or `null` when the page holds no tickets.
 * Pure: no source row is settled, no dialog state is touched.
 */
export function previewFromPlan(plan: ArrangementPlan): ArrangementPreviewOrder[] | null {

    if (!plan || plan.tickets.length === 0) {
        return null;
    }

    const previews: ArrangementPreviewOrder[] = plan.tickets.map(ticket => {

        const legs: ArrangementPreviewLeg[] = ticket.legs.map(leg => ({
            ticker: leg.wrapper.ticker,
            strike: leg.wrapper.strike,
            expiration: leg.wrapper.expiration,
            type: leg.wrapper.type,
            qty: leg.qty
        }));

        sortLegs(legs);

        return {
            title: undefined,
            legs,
            hcf: findHCF(legs.map(l => Math.abs(l.qty))),
            naked: ticket.shape === 'naked'
        };
    });

    sortBoxes(previews, p => p.legs);

    previews.forEach((preview, ix) => preview.title = zoneTitleForIndex(ix));

    return previews;
}
